import { MLAP_DASHBOARD_TEAL } from './chartColors'

// `description` and `source` feed the KpiCard info popover: description is the
// plain-language definition (with caveats), source becomes the origin badge.
// Range/comparison semantics and the lower-is-better rule are rendered
// visually by the popover, so the copy doesn't repeat them.
export const MLAP_STAT_CARDS = [
  {
    key: 'total_jobs',
    trendsGroup: 'business',
    playbookTag: 'service_titan',
    insights: [
      'Recurring weekly and monthly cycles show your natural demand curve — use them to time staffing and promos.',
      'Spikes that follow ad spend or review pushes show which channels actually book work.',
      'Read with GBP Impressions to separate demand (searches) from capture (booked jobs).'
    ],
    label: 'Total Jobs',
    icon: 'fa-solid fa-briefcase',
    color: 'primary-light',
    valueKey: 'totalJobs',
    prevValueKey: 'prevTotalJobs',
    growthKey: 'totalJobsGrowth',
    sparklineKey: 'jobsDaily',
    clickActionId: 'open-job-intel',
    source: 'ServiceTitan',
    description: 'Jobs completed across the locations in view, from ServiceTitan daily job stats. ' +
      'Click the card for the job intelligence breakdown.'
  },
  {
    key: 'total_revenue',
    trendsGroup: 'business',
    playbookTag: 'service_titan',
    insights: [
      'Diverging from Total Jobs means average ticket size is shifting — a pricing or job-mix change.',
      'Click into revenue intelligence to see which locations and job types drive the change.'
    ],
    label: 'Total Revenue',
    icon: 'fa-solid fa-dollar-sign',
    color: 'primary-light',
    valueKey: 'totalRevenueCents',
    prevValueKey: 'prevTotalRevenueCents',
    growthKey: 'totalRevenueGrowth',
    sparklineKey: 'revenueDaily',
    format: 'currency',
    clickActionId: 'open-revenue-intel',
    source: 'ServiceTitan',
    description: 'Job revenue summed across the locations in view. ' +
      'Click the card for the revenue intelligence breakdown.'
  },
  {
    key: 'avg_distance',
    trendsGroup: 'business',
    playbookTag: 'service_titan',
    insights: [
      'A creeping increase means crews drive further for the same work — margin quietly erodes.',
      'Jumps after service-area or dispatch changes reveal their real routing cost.',
      'Strong map rankings near base (SoLV) should pull jobs closer over time.'
    ],
    label: 'Avg Job Distance',
    icon: 'fa-solid fa-route',
    color: 'primary-light',
    valueKey: 'avgDistanceMiles',
    prevValueKey: 'prevAvgDistanceMiles',
    growthKey: 'avgDistanceGrowth',
    sparklineKey: 'distanceDaily',
    format: 'decimal',
    precision: 1,
    inverted: true,
    convertDistance: true,
    clickActionId: 'open-job-intel',
    source: 'ServiceTitan',
    description: 'Average distance between a location and its jobs, weighted by job count so busier days count more.'
  },
  {
    key: 'gbp_impressions',
    trendsGroup: 'seo',
    playbookTag: 'gbp',
    insights: [
      'Top-of-funnel local visibility: sustained drops often follow profile edits, suspensions, or new competitors.',
      'Seasonal demand shows here first — use it to time promotions.',
      'Impressions up with jobs flat points at conversion problems further down the funnel.'
    ],
    label: 'GBP Impressions',
    icon: 'fa-solid fa-map',
    color: 'primary-light',
    valueKey: 'gbpImpressions',
    prevValueKey: 'prevGbpImpressions',
    growthKey: 'gbpImpressionsGrowth',
    sparklineKey: 'gbpDaily',
    source: 'Google Business Profile',
    description: 'How many times these Business Profiles appeared to users on Google Maps and Google Search combined. ' +
      'Google reports impressions with a delay of a few days.'
  },
  {
    key: 'gsc_clicks',
    trendsGroup: 'seo',
    playbookTag: 'gsc',
    insights: [
      'Organic demand your pages actually capture — content and landing-page wins show here.',
      'Clicks falling while Avg Position holds suggests titles and snippets are losing the click.'
    ],
    label: 'GSC Clicks',
    icon: 'fa-solid fa-magnifying-glass',
    color: 'primary',
    valueKey: 'gscClicks',
    prevValueKey: 'prevGscClicks',
    growthKey: 'gscClicksGrowth',
    sparklineKey: 'gscDaily',
    source: 'Google Search Console',
    description: 'Clicks from Google organic search results to the pages assigned to these locations.'
  },
  {
    key: 'bing_clicks',
    trendsGroup: 'seo',
    insights: [
      'Confirms whether SEO gains carry beyond Google.',
      'A Google-only drop with Bing steady points at Google-specific issues, not your site.'
    ],
    label: 'Bing Clicks',
    icon: 'fa-brands fa-microsoft',
    color: 'primary',
    valueKey: 'bingClicks',
    prevValueKey: 'prevBingClicks',
    growthKey: 'bingClicksGrowth',
    sparklineKey: 'bingDaily',
    source: 'Bing Webmaster Tools',
    description: 'Clicks from Bing organic search results to the pages assigned to these locations.'
  },
  {
    key: 'google_ads_spend',
    trendsGroup: 'paid',
    insights: [
      'Steady daily spend means healthy budgets; a sawtooth pattern means budgets exhaust mid-day.',
      'Spend rising without Google Ads Leads rising is wasted budget — check campaign mapping and search terms.'
    ],
    // "Google Ads", not "Ad": LSA spend lives in its own cards (and is
    // excluded from the platform's google_ads stream), so this card must not
    // read as the all-products ads total.
    label: 'Google Ads Spend',
    icon: 'fa-brands fa-google',
    color: 'primary-light',
    valueKey: 'googleAdsSpendCents',
    prevValueKey: 'prevGoogleAdsSpendCents',
    growthKey: 'googleAdsSpendGrowth',
    sparklineKey: 'googleAdsDaily',
    format: 'currency',
    source: 'Google Ads',
    description: 'Spend for Google Ads campaigns mapped to the locations in view, across all campaign types except Local Services ' +
      '(tracked in the LSA cards). YouTube (video) campaigns are also broken out separately in their own cards.'
  },
  {
    key: 'google_ads_leads',
    trendsGroup: 'paid',
    insights: [
      'Divide Google Ads Spend by these leads for cost-per-lead — the number that decides scaling up or down.',
      'Step changes usually mean conversion-tracking changes, not real demand shifts.'
    ],
    label: 'Google Ads Leads',
    icon: 'fa-solid fa-user-plus',
    color: 'primary',
    valueKey: 'googleAdsConversions',
    prevValueKey: 'prevGoogleAdsConversions',
    growthKey: 'googleAdsConversionsGrowth',
    sparklineKey: 'googleAdsConversionsDaily',
    source: 'Google Ads',
    description: "Conversions reported by Google Ads for campaigns mapped to the locations in view, using each campaign's own conversion setup."
  },
  {
    key: 'youtube_ads_spend',
    trendsGroup: 'paid',
    insights: [
      'Awareness spend: expect it to lift branded searches and GBP Impressions more than direct leads.',
      'Read with YouTube Views for effective cost per view.'
    ],
    label: 'YouTube Spend',
    icon: 'fa-brands fa-youtube',
    color: 'primary-light',
    valueKey: 'youtubeAdsSpendCents',
    prevValueKey: 'prevYoutubeAdsSpendCents',
    growthKey: 'youtubeAdsSpendGrowth',
    sparklineKey: 'youtubeAdsDaily',
    format: 'currency',
    source: 'Google Ads · YouTube',
    description: 'Spend on YouTube (video) campaigns — the video slice of the same Google Ads connection, shown separately from the Google Ads Spend card.'
  },
  {
    key: 'youtube_ads_leads',
    trendsGroup: 'paid',
    insights: [
      'Video rarely converts last-click — low numbers can still be healthy if branded demand and GBP Impressions grow.'
    ],
    label: 'YouTube Leads',
    icon: 'fa-solid fa-user-plus',
    color: 'primary',
    valueKey: 'youtubeAdsConversions',
    prevValueKey: 'prevYoutubeAdsConversions',
    growthKey: 'youtubeAdsConversionsGrowth',
    sparklineKey: 'youtubeAdsConversionsDaily',
    source: 'Google Ads · YouTube',
    description: 'Conversions reported for YouTube (video) campaigns mapped to the locations in view.'
  },
  {
    key: 'youtube_ads_views',
    trendsGroup: 'paid',
    insights: [
      'Views holding while leads and site traffic stall suggests creative fatigue — rotate ads.'
    ],
    label: 'YouTube Views',
    icon: 'fa-solid fa-circle-play',
    color: 'primary',
    valueKey: 'youtubeAdsViews',
    prevValueKey: 'prevYoutubeAdsViews',
    growthKey: 'youtubeAdsViewsGrowth',
    sparklineKey: 'youtubeAdsViewsDaily',
    source: 'Google Ads · YouTube',
    description: 'Video views reported for YouTube campaigns mapped to the locations in view.'
  },
  {
    key: 'microsoft_ads_spend',
    trendsGroup: 'paid',
    insights: [
      'Usually cheaper clicks than Google — compare cost-per-lead across both before shifting budget.'
    ],
    label: 'Microsoft Ads Spend',
    icon: 'fa-brands fa-microsoft',
    color: 'primary-light',
    valueKey: 'microsoftAdsSpendCents',
    prevValueKey: 'prevMicrosoftAdsSpendCents',
    growthKey: 'microsoftAdsSpendGrowth',
    sparklineKey: 'microsoftAdsDaily',
    format: 'currency',
    source: 'Microsoft Advertising',
    description: 'Spend for Microsoft Advertising (Bing Ads) campaigns mapped to the locations in view.'
  },
  {
    key: 'microsoft_ads_leads',
    trendsGroup: 'paid',
    insights: [
      'Read against Microsoft Ads Spend for channel-level cost per lead.'
    ],
    label: 'Microsoft Ads Leads',
    icon: 'fa-solid fa-user-plus',
    color: 'primary',
    valueKey: 'microsoftAdsLeads',
    prevValueKey: 'prevMicrosoftAdsLeads',
    growthKey: 'microsoftAdsLeadsGrowth',
    sparklineKey: 'microsoftAdsLeadsDaily',
    source: 'Microsoft Advertising',
    description: 'Conversions reported by Microsoft Advertising (Bing Ads) for campaigns mapped to the locations in view.'
  },
  {
    key: 'meta_ads_spend',
    trendsGroup: 'paid',
    insights: [
      'Demand generation: watch for lift in jobs and reviews a few weeks after spend increases, not same-day.'
    ],
    label: 'Meta Spend',
    icon: 'fa-brands fa-meta',
    color: 'primary-light',
    valueKey: 'metaAdsSpendCents',
    prevValueKey: 'prevMetaAdsSpendCents',
    growthKey: 'metaAdsSpendGrowth',
    sparklineKey: 'metaAdsDaily',
    format: 'currency',
    source: 'Meta Ads',
    description: 'Meta (Facebook and Instagram) ad spend for campaigns mapped to the locations in view.'
  },
  {
    key: 'meta_ads_leads',
    trendsGroup: 'paid',
    insights: [
      'Meta leads cool fast — compare close rates against LSA and search leads before scaling.'
    ],
    label: 'Meta Leads',
    icon: 'fa-solid fa-comment-dots',
    color: 'primary',
    valueKey: 'metaAdsLeads',
    prevValueKey: 'prevMetaAdsLeads',
    growthKey: 'metaAdsLeadsGrowth',
    sparklineKey: 'metaAdsLeadsDaily',
    source: 'Meta Ads',
    description: 'Leads reported by Meta (Facebook and Instagram) ads for campaigns mapped to the locations in view.'
  },
  {
    key: 'nextdoor_ads_spend',
    insights: [
      'Neighborhood-level reach: watch for lift in reviews and referrals from nearby residents rather than same-day clicks.'
    ],
    label: 'Nextdoor Spend',
    icon: 'fa-solid fa-people-roof',
    color: 'primary-light',
    valueKey: 'nextdoorAdsSpendCents',
    prevValueKey: 'prevNextdoorAdsSpendCents',
    growthKey: 'nextdoorAdsSpendGrowth',
    sparklineKey: 'nextdoorAdsDaily',
    format: 'currency',
    source: 'Nextdoor Ads',
    description: 'Nextdoor ad spend for campaigns mapped to the locations in view.'
  },
  {
    key: 'nextdoor_ads_impressions',
    insights: [
      'Impressions rising while clicks stay flat points at creative or targeting, not neighborhood demand.'
    ],
    label: 'Nextdoor Impressions',
    icon: 'fa-solid fa-eye',
    color: 'primary',
    valueKey: 'nextdoorAdsImpressions',
    prevValueKey: 'prevNextdoorAdsImpressions',
    growthKey: 'nextdoorAdsImpressionsGrowth',
    sparklineKey: 'nextdoorAdsImpressionsDaily',
    source: 'Nextdoor Ads',
    description: 'How many times Nextdoor ads for campaigns mapped to the locations in view were shown to neighbors.'
  },
  {
    key: 'nextdoor_ads_clicks',
    insights: [
      'Read against Nextdoor Spend for channel-level cost per click.'
    ],
    label: 'Nextdoor Clicks',
    icon: 'fa-solid fa-arrow-pointer',
    color: 'primary',
    valueKey: 'nextdoorAdsClicks',
    prevValueKey: 'prevNextdoorAdsClicks',
    growthKey: 'nextdoorAdsClicksGrowth',
    sparklineKey: 'nextdoorAdsClicksDaily',
    source: 'Nextdoor Ads',
    description: 'Clicks on Nextdoor ads for campaigns mapped to the locations in view.'
  },
  {
    key: 'avg_position',
    trendsGroup: 'seo',
    playbookTag: 'gsc',
    insights: [
      'Slow, steady improvement validates SEO work; sudden drops usually mean algorithm updates or lost pages.',
      'Improving position with flat GSC Clicks points at weak titles and snippets.'
    ],
    label: 'Avg Position',
    icon: 'fa-solid fa-crosshairs',
    color: 'primary',
    valueKey: 'avgPosition',
    prevValueKey: 'prevAvgPosition',
    growthKey: 'avgPositionGrowth',
    sparklineKey: 'avgPositionDaily',
    format: 'decimal',
    inverted: true,
    source: 'Google Search Console',
    description: "Average position of these locations' pages in Google organic search results. 1 is the top result."
  },
  {
    key: 'ga4_sessions',
    trendsGroup: 'seo',
    playbookTag: 'ga4',
    insights: [
      'Total site demand across all channels — the denominator for conversion-rate questions.',
      'Sessions up with jobs flat suggests landing-page or booking friction.'
    ],
    label: 'GA4 Sessions',
    icon: 'fa-solid fa-chart-line',
    color: 'primary',
    valueKey: 'ga4Sessions',
    prevValueKey: 'prevGa4Sessions',
    growthKey: 'ga4SessionsGrowth',
    sparklineKey: 'ga4Daily',
    source: 'Google Analytics 4',
    description: 'Website sessions from the Google Analytics 4 properties assigned to the locations in view.'
  },
  {
    key: 'new_reviews',
    trendsGroup: 'reputation',
    playbookTag: 'reviews',
    insights: [
      'Review velocity is a leading indicator — slowdowns show up in map rankings months later.',
      'Spikes validate review-request campaigns; per-location gaps show who is not asking.'
    ],
    label: 'New Reviews',
    icon: 'fa-solid fa-star',
    color: 'primary-dark',
    valueKey: 'newReviews',
    prevValueKey: 'prevNewReviews',
    growthKey: 'newReviewsGrowth',
    sparklineKey: 'reviewsDaily',
    clickActionId: 'open-review-intel',
    source: 'Google Reviews',
    description: 'Google reviews received during the selected date range across the locations in view. ' +
      'Click the card for the review intelligence breakdown.'
  },
  {
    key: 'avg_rating',
    trendsGroup: 'reputation',
    playbookTag: 'reviews',
    insights: [
      'Dips flag service problems while they are still fixable — drill into locations and staff.',
      'Ratings below competitors cost map clicks even at equal rank.'
    ],
    label: 'Avg Rating',
    icon: 'fa-solid fa-star-half-stroke',
    color: 'primary-dark',
    valueKey: 'avgRating',
    prevValueKey: 'prevAvgRating',
    growthKey: 'avgRatingChange',
    sparklineKey: 'avgRatingDaily',
    format: 'decimal',
    growthFormat: 'absolute',
    source: 'Google Reviews',
    description: 'Average star rating of the reviews received during the selected date range — not the all-time profile rating. ' +
      'The change is shown in absolute stars rather than percent.'
  },
  {
    key: 'avg_atgr',
    trendsGroup: 'rankings',
    playbookTag: 'geogrids',
    insights: [
      'The map-visibility trend behind calls: improvements typically precede GBP action growth.',
      'Movements after profile or content changes show what actually moved rankings.'
    ],
    label: 'Avg ATGR',
    icon: 'fa-solid fa-map-location-dot',
    color: 'primary-dark',
    valueKey: 'avgRank',
    prevValueKey: 'prevAvgRank',
    growthKey: 'avgRankGrowth',
    sparklineKey: 'avgRankDaily',
    format: 'decimal',
    precision: 2,
    inverted: true,
    clickActionId: 'open-geogrid-intel',
    source: 'Geogrids',
    description: "Average Total Grid Rank from geogrid scans: the average Google Maps ranking across every point of a location's grid, " +
      'counting points where the business does not appear as worst-case. ' +
      'Averaged per location first, then across locations.'
  },
  {
    key: 'competitor_rating_gap',
    trendsGroup: 'reputation',
    playbookTag: 'audits',
    insights: [
      'A shrinking gap means competitors are catching up on reviews — respond before rank follows.'
    ],
    label: 'Rating vs Competitors',
    icon: 'fa-solid fa-scale-balanced',
    color: 'primary-dark',
    valueKey: 'competitorRatingGap',
    prevValueKey: 'prevCompetitorRatingGap',
    growthKey: 'competitorRatingGapChange',
    sparklineKey: 'competitorRatingGapDaily',
    format: 'signed_decimal',
    precision: 2,
    growthFormat: 'absolute',
    requiresFlag: 'hasAudits',
    clickActionId: 'open-review-intel',
    source: 'Competitor Audits',
    description: 'Your average review rating minus the median rating of the competitors tracked by audits for these locations. ' +
      'Positive means you out-rate them.'
  },
  {
    key: 'avg_solv',
    trendsGroup: 'rankings',
    playbookTag: 'geogrids',
    insights: [
      'Top-3 share is where map calls come from — small SoLV gains outperform large ATGR gains.',
      'Compare locations: low-SoLV outliers are the highest-leverage optimization targets.'
    ],
    label: 'Avg SoLV',
    icon: 'fa-solid fa-percent',
    color: 'primary-dark',
    valueKey: 'avgSolv',
    prevValueKey: 'prevAvgSolv',
    growthKey: 'avgSolvGrowth',
    sparklineKey: 'avgSolvDaily',
    format: 'percent',
    source: 'Geogrids',
    description: 'Share of Local Voice from geogrid scans: the percentage of grid points where the business ranks in the top 3 on Google Maps. ' +
      'Averaged per location first, then across locations.'
  },
  // LSA Spend/Leads are always visible (like the other ad-platform cards) so
  // the LSA vs Google Ads separation is legible even before an LSA account is
  // connected; the deeper LSA-only cards below stay behind hasLsa.
  {
    key: 'lsa_spend',
    trendsGroup: 'paid',
    playbookTag: 'lsa',
    insights: [
      'Sudden drops often mean account or verification issues rather than demand.'
    ],
    label: 'LSA Spend',
    icon: 'fa-solid fa-dollar-sign',
    color: 'primary-light',
    valueKey: 'lsaCostCents',
    prevValueKey: 'prevLsaCostCents',
    growthKey: 'lsaCostGrowth',
    sparklineKey: 'lsaCostDaily',
    format: 'currency',
    source: 'Local Services Ads',
    description: 'Google Local Services Ads spend for the LSA accounts assigned to the locations in view.'
  },
  {
    key: 'lsa_leads',
    trendsGroup: 'paid',
    playbookTag: 'lsa',
    insights: [
      'Lead flow reacts to bid changes and review count — watch this after either moves.'
    ],
    label: 'LSA Leads',
    icon: 'fa-solid fa-user-plus',
    color: 'primary',
    valueKey: 'lsaLeadsCount',
    prevValueKey: 'prevLsaLeadsCount',
    growthKey: 'lsaLeadsGrowth',
    sparklineKey: 'lsaLeadsDaily',
    source: 'Local Services Ads',
    description: 'All leads (calls, messages, and bookings) reported by Google Local Services Ads, including ones Google later credits. ' +
      'See Charged Leads for the billed subset.'
  },
  {
    key: 'lsa_charged_leads',
    trendsGroup: 'paid',
    playbookTag: 'lsa',
    insights: [
      'The gap between LSA Leads and this number is your dispute win rate — a widening gap means better credit hygiene.'
    ],
    label: 'Charged Leads',
    icon: 'fa-solid fa-circle-check',
    color: 'primary-dark',
    valueKey: 'lsaChargedLeadsCount',
    prevValueKey: 'prevLsaChargedLeadsCount',
    growthKey: 'lsaChargedLeadsGrowth',
    sparklineKey: 'lsaChargedLeadsDaily',
    requiresFlag: 'hasLsa',
    source: 'Local Services Ads',
    description: 'Local Services Ads leads Google actually billed for — leads with a pending credit request, credited leads, ' +
      'and leads Google never charged are excluded.'
  },
  {
    key: 'lsa_cost_per_lead',
    trendsGroup: 'paid',
    playbookTag: 'lsa',
    insights: [
      'The scaling decision number: compare against average job value to set a bid ceiling.',
      'Rising cost-per-lead with steady spend means competition is heating up in your categories.'
    ],
    label: 'LSA Cost/Lead',
    icon: 'fa-solid fa-coins',
    color: 'primary-light',
    valueKey: 'lsaCostPerLeadCents',
    prevValueKey: 'prevLsaCostPerLeadCents',
    growthKey: 'lsaCostPerLeadGrowth',
    sparklineKey: 'lsaCostPerLeadDaily',
    format: 'currency',
    inverted: true,
    requiresFlag: 'hasLsa',
    source: 'Local Services Ads',
    description: 'Local Services Ads spend divided by charged leads.'
  }
]

export const MLAP_STAT_CARD_KEYS = MLAP_STAT_CARDS.map((c) => c.key)

// Dashboard KPI sparklines reuse the MLAP dashboard teal theme so the
// sparkline strokes match the surrounding KPI card accents painted by
// `pages/mlap/dashboard-teal.scss`. Same scale, one source.
export const SPARKLINE_COLORS = {
  'primary-light': MLAP_DASHBOARD_TEAL[400],
  primary: MLAP_DASHBOARD_TEAL[500],
  'primary-dark': MLAP_DASHBOARD_TEAL[600]
}
